import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useFetch, api, fmtDate } from "@/lib/shieldx";
import { PageHeader, Panel, Loader } from "@/components/shieldx/Primitives";
import { DemoTag, RiskBadge } from "@/components/shieldx/Badges";
import { Table } from "@/components/shieldx/Table";
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer, Tooltip,
} from "recharts";
import {
  ShieldCheck, ArrowLeft, Download, FileText, CheckCircle2, AlertTriangle, Clock,
  TrendingUp, ListChecks, Fingerprint, ArrowRight, Crosshair,
} from "lucide-react";
import { toast } from "sonner";

const FACTORS = ["detection", "authorization", "policy", "blocking", "containment", "response_time", "auditability", "recovery_readiness"];

function ratingTone(level) {
  if (level === "EXCELLENT") return { text: "text-emerald-400", bar: "bg-emerald-500", ring: "border-emerald-500/40" };
  if (level === "STRONG") return { text: "text-blue-400", bar: "bg-blue-500", ring: "border-blue-500/40" };
  if (level === "NEEDS IMPROVEMENT") return { text: "text-amber-400", bar: "bg-amber-500", ring: "border-amber-500/40" };
  if (level === "HIGH RISK") return { text: "text-orange-400", bar: "bg-orange-500", ring: "border-orange-500/40" };
  return { text: "text-red-400", bar: "bg-red-500", ring: "border-red-500/40" };
}
function barColor(v) {
  if (v >= 90) return "bg-emerald-500";
  if (v >= 75) return "bg-blue-500";
  if (v >= 60) return "bg-amber-500";
  if (v >= 40) return "bg-orange-500";
  return "bg-red-500";
}

function Meta({ label, value }) {
  return (
    <div>
      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm text-slate-200">{value || "—"}</p>
    </div>
  );
}

function RatingDial({ value, level, tone }) {
  const [display, setDisplay] = useState(0);
  const R = 72, C = 2 * Math.PI * R;
  const stroke = value >= 75 ? "#34d399" : value >= 60 ? "#fbbf24" : value >= 40 ? "#fb923c" : "#f87171";
  useEffect(() => {
    let raf, start;
    const dur = 1100;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / dur, 1);
      setDisplay(Math.round((1 - Math.pow(1 - p, 3)) * value));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return (
    <div className="relative mx-auto mt-3 flex h-[200px] w-[200px] items-center justify-center" data-testid="scorecard-dial">
      <svg width="200" height="200" className="-rotate-90">
        <circle cx="100" cy="100" r={R} fill="none" stroke="#1e293b" strokeWidth="12" />
        <motion.circle cx="100" cy="100" r={R} fill="none" stroke={stroke} strokeWidth="12" strokeLinecap="round"
          strokeDasharray={C} initial={{ strokeDashoffset: C }} animate={{ strokeDashoffset: C - (value / 100) * C }}
          transition={{ duration: 1.1, ease: "easeOut" }} style={{ filter: `drop-shadow(0 0 6px ${stroke}66)` }} />
      </svg>
      <div className="absolute flex flex-col items-center">
        <span className={`font-display text-5xl font-extrabold tabular-nums ${tone.text}`}>{display}</span>
        <span className="font-mono text-[10px] text-slate-600">/ 100</span>
        <span className={`mt-1 font-display text-sm font-bold tracking-wide ${tone.text}`}>{level}</span>
      </div>
    </div>
  );
}

const REPORT_TYPES = [
  { key: "executive", label: "Executive" },
  { key: "technical", label: "Technical" },
  { key: "audit", label: "Audit" },
  { key: "defense", label: "Defense Validation" },
];

export default function Scorecard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: sc, loading } = useFetch(`/simulations/${id}`);
  const [reportType, setReportType] = useState("defense");
  const [busy, setBusy] = useState(false);

  if (loading || !sc) return <Loader label="Loading security rating scorecard" />;
  const tone = ratingTone(sc.level);
  const b = sc.breakdown;
  const ts = sc.tech_summary;

  const download = async (format) => {
    setBusy(true);
    try {
      if (format === "pdf") {
        const res = await api.get(`/simulations/${sc.sim_id}/report`, { params: { type: reportType, format: "pdf" }, responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url; a.download = `ShieldX-${sc.sim_id}-${reportType}.pdf`; a.click();
        URL.revokeObjectURL(url);
      } else {
        const res = await api.get(`/simulations/${sc.sim_id}/report`, { params: { type: reportType, format: "json" } });
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `ShieldX-${sc.sim_id}-${reportType}.json`; a.click();
        URL.revokeObjectURL(url);
      }
      toast.success(`${reportType} report (${format.toUpperCase()}) downloaded`);
    } catch {
      toast.error("Report generation failed");
    } finally { setBusy(false); }
  };

  const flowEvent = sc.findings[0] || null;

  return (
    <div>
      <button onClick={() => navigate("/app/simulation")} data-testid="scorecard-back"
        className="mb-4 flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200">
        <ArrowLeft className="h-4 w-4" /> Back to Attack Simulation
      </button>

      <PageHeader icon={ShieldCheck} title="ShieldX Security Rating"
        subtitle="Defense Validation Based on Controlled Simulation" actions={<DemoTag />} />

      {/* 1. Header meta */}
      <Panel testid="scorecard-header">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <Meta label="Organization" value={sc.organization} />
          <Meta label="Protected Asset" value={sc.asset_name} />
          <Meta label="Asset ID" value={<span className="font-mono">{sc.asset_sx_id}</span>} />
          <Meta label="Simulation ID" value={<span className="font-mono">{sc.sim_id}</span>} />
          <Meta label="Simulation Date" value={fmtDate(sc.simulation_date)} />
          <Meta label="Environment" value={sc.environment} />
          <Meta label="Duration" value={sc.duration} />
          <Meta label="Authorized By" value={sc.authorized_by} />
        </div>
        <div className="mt-4 inline-flex items-center gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 font-mono text-[11px] uppercase tracking-widest text-emerald-300">
          <CheckCircle2 className="h-3.5 w-3.5" /> {sc.status}
        </div>
      </Panel>

      {/* 2 + 3. Rating dial + radar breakdown */}
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <div className={`rounded-xl border bg-slate-900/50 p-6 ${tone.ring}`} data-testid="scorecard-rating">
          <p className="text-center font-mono text-[10px] uppercase tracking-widest text-slate-500">ShieldX Defense Rating</p>
          <RatingDial value={sc.overall} level={sc.level} tone={tone} />
          <p className="mt-4 text-left text-[11px] leading-relaxed text-slate-500">{sc.disclaimer}</p>
        </div>

        <Panel title="Dimension Radar" desc="Computed only from this simulation — MEASURED evidence" className="lg:col-span-2" testid="scorecard-breakdown">
          <ResponsiveContainer width="100%" height={300}>
            <RadarChart data={FACTORS.map((f) => ({ dim: sc.breakdown_labels[f], score: b[f] }))} outerRadius="72%">
              <PolarGrid stroke="#1e293b" />
              <PolarAngleAxis dataKey="dim" tick={{ fill: "#94a3b8", fontSize: 10 }} />
              <Radar name="Score" dataKey="score" stroke="#3b82f6" strokeWidth={2} fill="#3b82f6" fillOpacity={0.3} isAnimationActive animationDuration={900} />
              <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} />
            </RadarChart>
          </ResponsiveContainer>
          <div className="mt-2 grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
            {FACTORS.map((f) => (
              <div key={f} className="flex items-center justify-between text-xs">
                <span className="text-slate-400">{sc.breakdown_labels[f]}</span>
                <span className={`font-mono font-semibold ${b[f] >= 75 ? "text-emerald-400" : b[f] >= 50 ? "text-amber-400" : "text-rose-400"}`}>{b[f]}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* 5 + 4. Validated controls + defense gaps */}
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel title="Protection Controls Validated" desc={`${sc.validated} of ${sc.total_scenarios} scenarios`} testid="scorecard-validated">
          {sc.validated_controls.length ? (
            <ul className="space-y-2">
              {sc.validated_controls.map((v, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm text-slate-300">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" />
                  <span>{v.label} <span className="block font-mono text-[10px] uppercase tracking-wide text-slate-500">{v.control}</span></span>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm text-slate-500">No fully-validated controls in this run.</p>}
        </Panel>

        <Panel title="Defense Gaps Identified" desc={`${sc.gaps} gap${sc.gaps !== 1 ? "s" : ""}`} testid="scorecard-gaps">
          {sc.findings.length ? (
            <div className="space-y-3">
              {sc.findings.map((f) => (
                <div key={f.finding_id} className="rounded-lg border border-slate-800 bg-slate-950/40 p-3.5">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-slate-400">{f.finding_id}</span>
                    <RiskBadge value={f.severity} />
                  </div>
                  <p className="mt-1.5 text-sm font-semibold text-slate-200">{f.category}</p>
                  <p className="mt-1 text-xs text-slate-400">{f.observation}</p>
                  <p className="mt-1.5 text-xs text-slate-300"><span className="text-slate-500">Observed:</span> {f.observed_result} · <span className="text-slate-500">Impact:</span> {f.impact}</p>
                  <p className="mt-1.5 text-xs text-blue-300">→ {f.recommendation}</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-2 rounded-lg border border-emerald-500/20 bg-emerald-500/5 px-3 py-4 text-sm text-emerald-300">
              <CheckCircle2 className="h-4 w-4" /> No defense gaps identified in this controlled simulation.
            </div>
          )}
        </Panel>
      </div>

      {/* 8. Attack / defense flow */}
      {flowEvent && (
        <Panel title="Red Team → Blue Team → Decision" desc="Traceable to a simulation event" className="mt-4" testid="scorecard-flow">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {[`Simulated ${flowEvent.category}`, flowEvent.scenario, "Authorization Check", "Policy Evaluation", flowEvent.observed_result].map((s, i, arr) => (
              <div key={i} className="flex items-center gap-2">
                <span className="rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-2 font-mono uppercase tracking-wide text-slate-300">{s}</span>
                {i < arr.length - 1 && <ArrowRight className="h-3.5 w-3.5 text-slate-600" />}
              </div>
            ))}
          </div>
          <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-amber-400">Status: Defense Gap Identified</p>
        </Panel>
      )}

      {/* 6 + 7. Proof log + performance */}
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Panel title="Simulation Proof Log" desc="Controlled technical timeline" className="lg:col-span-2" testid="scorecard-proof">
          <ol className="relative space-y-3 border-l border-slate-800 pl-5">
            {sc.proof_log.map((p, i) => (
              <li key={i} className="relative">
                <span className="absolute -left-[22px] top-1 h-2 w-2 rounded-full bg-blue-500" />
                <span className="font-mono text-[10px] text-slate-500">{new Date(p.ts).toISOString().substr(11, 8)} UTC</span>
                <span className="ml-2 text-xs text-slate-300">{p.detail}</span>
                {p.evt && <span className="ml-1 font-mono text-[10px] text-slate-600">[{p.evt}]</span>}
              </li>
            ))}
          </ol>
        </Panel>

        <Panel title="Defense Performance" desc="Measured control timings" testid="scorecard-performance">
          <div className="space-y-2.5">
            {Object.entries(sc.performance).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between border-b border-slate-800/60 pb-2 text-sm">
                <span className="capitalize text-slate-400">{k.replace(/_/g, " ")}</span>
                <span className="font-mono text-slate-200">{v}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      {/* 12. Before / after */}
      {sc.before_after && (
        <Panel title="Before / After Validation" desc={`Compared to ${sc.before_after.prev_sim_id}`} className="mt-4" testid="scorecard-before-after">
          <div className="grid gap-4 sm:grid-cols-2">
            {["before", "after"].map((k) => (
              <div key={k} className="rounded-lg border border-slate-800 bg-slate-950/40 p-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{k}</p>
                <p className={`mt-1 font-display text-3xl font-bold ${k === "after" ? ratingTone(sc.level).text : "text-slate-400"}`}>{sc.before_after[k].rating}<span className="text-base text-slate-600">/100</span></p>
                <div className="mt-2 space-y-1 text-xs text-slate-400">
                  {["detection", "authorization", "blocking", "response_time"].map((f) => (
                    <div key={f} className="flex justify-between"><span className="capitalize">{f.replace(/_/g, " ")}</span><span className="font-mono text-slate-300">{sc.before_after[k][f]}</span></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-emerald-400"><TrendingUp className="h-3.5 w-3.5" /> {sc.before_after.label}</p>
        </Panel>
      )}

      {/* 10 + 11. Recommendations + protection recommendation */}
      {sc.recommendations.length > 0 && (
        <Panel title="Recommended Actions" desc="Prioritized by severity" className="mt-4" testid="scorecard-recommendations">
          <div className="space-y-2.5">
            {sc.recommendations.map((r, i) => (
              <div key={i} className="flex items-start gap-3 rounded-lg border border-slate-800 bg-slate-950/40 p-3">
                <RiskBadge value={r.priority} />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-slate-200">{r.title}</p>
                  <p className="text-xs text-slate-400">{r.action}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => navigate("/app/policies")} className="rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1 text-[11px] font-medium text-slate-200 hover:bg-slate-700">Review Policy</button>
                  <button onClick={() => navigate("/app/protection")} className="rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1 text-[11px] font-medium text-slate-200 hover:bg-slate-700">Configure Protection</button>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}
      {sc.protection_recommendation && (
        <div className="mt-4 flex flex-col items-start gap-3 rounded-xl border border-blue-500/30 bg-blue-600/10 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="scorecard-protection-rec">
          <div className="flex items-start gap-2.5">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-blue-400" />
            <p className="text-sm text-slate-200">{sc.protection_recommendation}</p>
          </div>
          <button onClick={() => navigate("/app/protection")} className="whitespace-nowrap rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500">Activate ShieldX Protection</button>
        </div>
      )}

      {/* 13. Findings severity table */}
      {sc.findings.length > 0 && (
        <Panel title="Findings & Evidence Traceability" desc="Every finding traces to a simulation event" className="mt-4" testid="scorecard-findings-table">
          <Table testid="scorecard-findings-rows"
            columns={[
              { key: "finding_id", label: "Finding", render: (r) => <span className="font-mono text-xs text-slate-300">{r.finding_id}</span> },
              { key: "severity", label: "Severity", render: (r) => <RiskBadge value={r.severity} /> },
              { key: "category", label: "Category", render: (r) => <span className="text-slate-200">{r.category}</span> },
              { key: "status", label: "Status", render: (r) => <span className="font-mono text-[11px] text-amber-300">{r.status}</span> },
              { key: "trace", label: "Evidence Chain", render: (r) => <span className="font-mono text-[10px] text-slate-500">{r.trace.event} · {r.trace.incident} · {r.trace.policy}</span> },
            ]}
            rows={sc.findings} />
        </Panel>
      )}

      {/* 14 + 15. Summaries */}
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Panel title="Executive Summary" testid="scorecard-exec">
          <p className="text-sm leading-relaxed text-slate-300">{sc.exec_summary}</p>
        </Panel>
        <Panel title="Technical Summary" testid="scorecard-tech">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {[["Total Scenarios", ts.total_scenarios], ["Successful Defenses", ts.successful_defenses], ["Defense Gaps", ts.defense_gaps], ["Blocked", ts.blocked], ["Challenged", ts.challenged], ["Rate Limited", ts.rate_limited], ["Isolated", ts.isolated], ["Incidents", ts.incidents], ["Policies Triggered", ts.policies_triggered], ["Avg Response", ts.avg_response_time]].map(([k, v]) => (
              <div key={k} className="flex justify-between border-b border-slate-800/60 pb-1.5"><span className="text-slate-400">{k}</span><span className="font-mono text-slate-200">{v}</span></div>
            ))}
          </div>
        </Panel>
      </div>

      {/* 16. Reports */}
      <Panel title="Generate Report" desc="Executive · Technical · Audit · Defense Validation — PDF / JSON / Web" className="mt-4" testid="scorecard-reports">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Report Type</label>
            <select data-testid="report-type-select" value={reportType} onChange={(e) => setReportType(e.target.value)}
              className="mt-1.5 rounded-lg border border-slate-700 bg-slate-950/60 px-3 py-2 text-sm text-slate-200 focus:border-blue-500 focus:outline-none">
              {REPORT_TYPES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          <button onClick={() => download("pdf")} disabled={busy} data-testid="report-pdf-btn"
            className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-500 disabled:opacity-40">
            <Download className="h-4 w-4" /> PDF
          </button>
          <button onClick={() => download("json")} disabled={busy} data-testid="report-json-btn"
            className="flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800/60 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-700 disabled:opacity-40">
            <FileText className="h-4 w-4" /> JSON
          </button>
        </div>
        <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-slate-600">Every report reflects only the scenarios executed within the authorized simulation scope · Controlled Security Simulation</p>
      </Panel>

      {/* 23. Final message */}
      <div className="mt-6 rounded-xl border border-slate-800 bg-slate-900/40 p-6 text-center">
        <p className="font-display text-base font-semibold text-slate-100">Security is not a claim. It is something you validate.</p>
        <p className="mt-1.5 font-mono text-sm uppercase tracking-wider text-blue-400/90">Run. Observe. Improve. Validate Again.</p>
        <p className="mt-3 font-mono text-[11px] tracking-wide text-slate-600">ShieldX · Capability Does Not Equal Authority · Powered by Vijay Software Solutions Pvt Ltd</p>
      </div>
    </div>
  );
}
